import { LineSeries } from 'lightweight-charts';
import { useCallback, useEffect, useMemo } from 'react';
import {
  isVwapIndicatorUpdate,
  VwapIndicatorUpdate
} from '../services/indicator-update.service';
import { IndicatorType } from '@shared/types';
import { useIndicatorUpdateStream } from './use-indicator-update-stream.hook';
import { useChartContext } from '../components/chart.context';

export const useVwapSeries = () => {
  const {
    chartRef,
    overlayIndicatorSeriesRefs: indicatorSeriesRefs,
    chartInitialized,
    data: { indicatorConfigs }
  } = useChartContext();

  const hasVwap = useMemo(
    () =>
      indicatorConfigs.overlay.some(
        (indicator) => indicator.type === IndicatorType.VWAP
      ),
    [indicatorConfigs.overlay]
  );

  const getOrCreateSeries = useCallback((index: number) => {
    if (!chartRef.current) return null;

    if (!indicatorSeriesRefs.current[IndicatorType.VWAP]) {
      indicatorSeriesRefs.current[IndicatorType.VWAP] = [];
    }

    const vwapSeries = indicatorSeriesRefs.current[IndicatorType.VWAP];
    if (!vwapSeries) return null;

    if (!vwapSeries[index]) {
      vwapSeries[index] = chartRef.current.addSeries(LineSeries, {
        color: '#00e5ff',
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false
      });
    }

    return vwapSeries[index] ?? null;
  }, []);

  const applyUpdate = useCallback(
    (update: VwapIndicatorUpdate) => {
      const series = getOrCreateSeries(update.indicatorIndex);
      if (!series) return;

      if (update.updateType === 'snapshot') {
        series.setData(
          update.data.map((item) =>
            typeof item.value === 'number'
              ? { time: item.time, value: item.value }
              : { time: item.time }
          )
        );
        return;
      }

      update.data.forEach((item) => {
        series.update(
          typeof item.value === 'number'
            ? { time: item.time, value: item.value }
            : { time: item.time }
        );
      });
    },
    [getOrCreateSeries]
  );

  const clearVwapSeries = useCallback(() => {
    const vwapSeries = indicatorSeriesRefs.current[IndicatorType.VWAP];
    if (!vwapSeries?.length) return;

    vwapSeries.forEach((series) => {
      if (!series) return;
      chartRef.current?.removeSeries(series);
    });

    indicatorSeriesRefs.current[IndicatorType.VWAP] = [];
  }, [chartRef, indicatorSeriesRefs]);

  useEffect(() => {
    if (hasVwap) return;
    clearVwapSeries();
  }, [clearVwapSeries, hasVwap]);

  useIndicatorUpdateStream({
    chartInitialized,
    enabled: hasVwap,
    indicatorType: IndicatorType.VWAP,
    isExpectedUpdate: isVwapIndicatorUpdate,
    applyUpdate
  });
};
